/**
 * ConfidenceChip — surfaces the retrieval confidence next to an ARIA answer.
 *
 * Colour bands map to the backend's `confidence.band`:
 *   high      -> teal accent, "Grounded"
 *   moderate  -> amber, "Partial match"
 *   low       -> soft magenta, "Weak match"
 *   uncertain -> muted, "Not in library"
 *
 * Hovering the chip surfaces the underlying signals (coverage, strength,
 * matched keywords) so a skeptical scientist can inspect *why* ARIA is
 * confident, not just the number.
 */
import { ShieldCheck, ShieldAlert, ShieldQuestion, Shield } from "lucide-react";

const BAND_STYLE = {
  high: {
    label: "Grounded",
    Icon: ShieldCheck,
    className: "text-emerald-400 border-emerald-500/40 bg-emerald-500/8",
  },
  moderate: {
    label: "Partial match",
    Icon: Shield,
    className: "text-amber-300 border-amber-500/40 bg-amber-500/8",
  },
  low: {
    label: "Weak match",
    Icon: ShieldAlert,
    className: "text-[var(--aria-primary)] border-[var(--aria-primary)]/40 bg-[var(--aria-primary)]/8",
  },
  uncertain: {
    label: "Not in library",
    Icon: ShieldQuestion,
    className: "text-[var(--aria-text-muted)] border-[var(--aria-border)] bg-[var(--aria-surface-2)]/60",
  },
};

export default function ConfidenceChip({ confidence, testId }) {
  if (!confidence || typeof confidence.score !== "number") return null;
  const band = confidence.band || "uncertain";
  const style = BAND_STYLE[band] || BAND_STYLE.uncertain;
  const Icon = style.Icon;
  const pct = Math.round((confidence.score || 0) * 100);

  const tooltip = [
    `Coverage: ${Math.round((confidence.coverage || 0) * 100)}%`,
    `Strength: ${Math.round((confidence.strength || 0) * 100)}%`,
    confidence.matched_keywords?.length
      ? `Matched: ${confidence.matched_keywords.join(", ")}`
      : null,
    confidence.missing_keywords?.length
      ? `Missing: ${confidence.missing_keywords.join(", ")}`
      : null,
  ].filter(Boolean).join(" · ");

  return (
    <span
      data-testid={testId || "confidence-chip"}
      data-band={band}
      title={tooltip}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.16em] rounded-sm border ${style.className}`}
    >
      <Icon size={11} />
      <span>{style.label}</span>
      <span className="opacity-70 tabular-nums">· {pct}%</span>
    </span>
  );
}
